import { useTranslation } from 'react-i18next';

import { Icon } from '../../components/Icon';
import { RichTextEditor } from '../../components/RichTextEditor';
import type { KbSection } from './sections';

interface SectionEditorProps {
  section: KbSection;
  index: number;
  total: number;
  onChange: (section: KbSection) => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}

/**
 * Ein Abschnitt eines Wissensartikels in der Maske -- F4-26.
 *
 * **Was hier anders ist als vorher.**
 *
 * Der Abschnitt ist eine eigene Flaeche. Vorher standen sechs Aufschriften
 * und sechs Textfelder untereinander ohne erkennbare Grenze -- beim Rollen
 * war nicht mehr zu sehen, welches Feld zu welcher Aufschrift gehoert.
 *
 * Das Titelfeld traegt seine eigene Beschriftung. Vorher lag es **in** einem
 * ``<label for="...">``, das auf das Textfeld darunter zeigte: Ein
 * vorlesendes Programm sagte damit den Titel des einen Feldes zum anderen an,
 * und ein Klick auf die Aufschrift setzte die Schreibmarke ins falsche Feld.
 *
 * Geschrieben wird im ``RichTextEditor`` -- demselben wie am Ticket und in
 * jeder anderen Maske. Vorher stand hier eine eigene Formatierleiste mit
 * sieben Knoepfen ueber einem Textfeld, in dem das HTML sichtbar war; am
 * Ticket waren es drei andere. Wer zwischen beiden Masken wechselte, musste
 * jedes Mal neu lernen, was geht.
 *
 * **Ohne Bildknopf.** Ein Wissensartikel hat keinen Anhangsbestand, aus dem
 * ein Bild kommen koennte -- und ein Knopf, der nichts tun kann, ist
 * schlimmer als keiner.
 */
export function SectionEditor({
  section,
  index,
  total,
  onChange,
  onMove,
  onRemove,
}: SectionEditorProps) {
  const { t } = useTranslation();

  return (
    <div className="kb-section-editor">
      <div className="kb-section-editor__head">
        <input
          className="field__control"
          aria-label={t('knowledge.sectionHeading', { number: index + 1 })}
          value={section.heading}
          onChange={(event) => {
            onChange({ ...section, heading: event.target.value });
          }}
        />
        <button
          type="button"
          className="kb-format__button"
          disabled={index === 0}
          aria-label={t('knowledge.moveSectionUp', { heading: section.heading })}
          onClick={() => {
            onMove(-1);
          }}
        >
          <Icon name="arrowUp" />
        </button>
        <button
          type="button"
          className="kb-format__button"
          disabled={index === total - 1}
          aria-label={t('knowledge.moveSectionDown', { heading: section.heading })}
          onClick={() => {
            onMove(1);
          }}
        >
          <Icon name="arrowDown" />
        </button>
        <button
          type="button"
          className="kb-format__button"
          aria-label={t('knowledge.removeSection', { heading: section.heading })}
          onClick={onRemove}
        >
          {'×'}
        </button>
      </div>

      <RichTextEditor
        value={section.body_html}
        onChange={(html) => {
          onChange({ ...section, body_html: html });
        }}
        ariaLabel={t('knowledge.sectionBody', { heading: section.heading })}
        features={{ image: false }}
      />
    </div>
  );
}
